import React, { useState } from 'react';
import { Box, Divider, LinearProgress, Tooltip, Typography } from '@mui/material';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { PlannerAction, PlannerState, Waypoint } from '../types';
import { formatArrival, formatDistance, formatDuration } from '../geo/format';
import WaypointList from './WaypointList';
import NamedRoutes, { NamedRoutesProps } from './NamedRoutes';
import RouteOptionsDialog from './RouteOptionsDialog';
import RouteFilesDialog from './RouteFilesDialog';

const GOLD = '#f5b942';

const PROFILE_LABEL: Record<string, string> = {
  'driving-traffic': 'Driving · traffic',
  driving: 'Driving',
  walking: 'Walking',
  cycling: 'Cycling',
};

const Action: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  accent?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, accent, title, children }) => (
  <Tooltip title={title || ''} disableHoverListener={!title}>
    <Typography
      component="span"
      role="button"
      tabIndex={disabled ? -1 : 0}
      variant="caption"
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); }
      }}
      sx={{
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'text.disabled' : accent ? GOLD : 'text.primary',
        '&:hover': disabled ? undefined : { textDecoration: 'underline' },
        '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
      }}
    >
      {children}
    </Typography>
  </Tooltip>
);

/**
 * The editing half of the route tool: stops, options, and what to do with the
 * result. Everything an operator *reads* rather than changes — directions,
 * places, events, corridor findings, the live run — lives in the reference dock
 * on the other side of the map, so that editing a route and consulting it are
 * no longer competing for the same column.
 */
export interface RoutePlannerPanelProps {
  state: PlannerState;
  dispatch: React.Dispatch<PlannerAction>;
  canUndo: boolean;
  canRedo: boolean;
  proximity?: { longitude: number; latitude: number } | null;
  saveLabel?: string;
  saving?: boolean;
  onSave?: () => void;
  onRetry: () => void;
  onFocusWaypoint: (waypoint: Waypoint) => void;
  onFlyTo?: (longitude: number, latitude: number) => void;
  missionId?: number | null;
  routeName?: string;
  /** Extra actions contributed by the host. */
  extraActions?: React.ReactNode;
  namedSlots?: NamedRoutesProps['slots'];
  editingSlotId?: string | null;
  onSelectNamedSlot?: (id: string) => void;
  onAddNamedSlot?: () => void;
  onUseNamedSlot?: (id: string) => void;
  onDeleteNamedSlot?: (id: string) => void;
  canAddNamedSlot?: boolean;
}

const RoutePlannerPanel: React.FC<RoutePlannerPanelProps> = ({
  state,
  dispatch,
  canUndo,
  canRedo,
  proximity,
  saveLabel = 'Save route',
  saving,
  onSave,
  onRetry,
  onFocusWaypoint,
  onFlyTo,
  missionId,
  routeName,
  extraActions,
  namedSlots,
  editingSlotId,
  onSelectNamedSlot,
  onAddNamedSlot,
  onUseNamedSlot,
  onDeleteNamedSlot,
  canAddNamedSlot,
}) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const routing = state.phase === 'routing';
  const primary = state.routes[state.primaryIndex] || null;
  const arrival = primary ? formatArrival(state.options.departAt, primary.duration) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Route</Typography>
        <Action onClick={() => setOptionsOpen(true)} title="Profile, avoidances, departure, units">
          {PROFILE_LABEL[state.options.profile]}
        </Action>
        <Tooltip title="Undo (Ctrl+Z)">
          <Box
            component="span"
            onClick={() => canUndo && dispatch({ type: 'UNDO' })}
            sx={{ display: 'flex', cursor: canUndo ? 'pointer' : 'default', color: canUndo ? 'text.primary' : 'text.disabled' }}
          >
            <UndoIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Shift+Z)">
          <Box
            component="span"
            onClick={() => canRedo && dispatch({ type: 'REDO' })}
            sx={{ display: 'flex', cursor: canRedo ? 'pointer' : 'default', color: canRedo ? 'text.primary' : 'text.disabled' }}
          >
            <RedoIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      </Box>

      <WaypointList
        waypoints={state.waypoints}
        dispatch={dispatch}
        proximity={proximity}
        onFocusWaypoint={onFocusWaypoint}
        onFlyTo={onFlyTo}
      />

      {state.waypoints.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Search an address, use your location, or click the map. Drag the line to pull in a stop.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        <Action
          disabled={state.waypoints.length < 2}
          onClick={() => dispatch({ type: 'REVERSE_ROUTE' })}
        >
          Reverse
        </Action>
        <Action
          disabled={state.waypoints.length === 0}
          onClick={() => dispatch({ type: 'CLEAR' })}
        >
          Clear
        </Action>
        <Action onClick={() => setFilesOpen(true)} title="Save, export, import, share">
          Files
        </Action>
        {onSave && (
          <Action
            accent
            disabled={!state.routes.length || !!saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : saveLabel}
          </Action>
        )}
        {extraActions}
      </Box>

      {state.error && (
        <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: '#e0563d' }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#e0563d' }}>
            {state.error.message}
          </Typography>
          {state.error.retryable && (
            <Action accent onClick={onRetry}>Try again</Action>
          )}
        </Box>
      )}

      {state.failedLegs.map((leg) => (
        <Typography
          key={`${leg.fromIndex}-${leg.toIndex}`}
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: '#e0563d' }}
        >
          No road route between stop {leg.fromIndex + 1} and {leg.toIndex + 1}. Shown as a straight dashed line.
        </Typography>
      ))}

      {(primary || routing) && <Divider sx={{ my: 1.25 }} />}

      {routing && (
        <LinearProgress sx={{ height: 2, mb: 0.75, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }} />
      )}

      {/* Just the headline. The full readout is one glance to the right, and
          repeating it here would put the same numbers in two places that can
          disagree while a new route is being fetched. */}
      {primary && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {formatDuration(primary.duration)}
          </Typography>
          <Typography variant="body2">
            {formatDistance(primary.distance, state.options.units)}
          </Typography>
          {arrival && (
            <Typography variant="caption" color="text.secondary">arrive {arrival}</Typography>
          )}
        </Box>
      )}

      <NamedRoutes
        state={state}
        dispatch={dispatch}
        slots={namedSlots}
        editingId={editingSlotId}
        onSelectSlot={onSelectNamedSlot}
        onAddSlot={onAddNamedSlot}
        onUseSlot={onUseNamedSlot}
        onDeleteSlot={onDeleteNamedSlot}
        canAdd={canAddNamedSlot}
        saveLabel={saveLabel}
        saving={saving}
        onSave={onSave}
      />

      <RouteFilesDialog
        open={filesOpen}
        onClose={() => setFilesOpen(false)}
        state={state}
        dispatch={dispatch}
        missionId={missionId}
        defaultName={routeName}
      />

      <RouteOptionsDialog
        open={optionsOpen}
        options={state.options}
        onClose={() => setOptionsOpen(false)}
        onChange={(next) => {
          if (next.units && Object.keys(next).length === 1) {
            dispatch({ type: 'SET_UNITS', units: next.units });
            return;
          }
          dispatch({ type: 'SET_OPTIONS', options: next });
        }}
      />
    </Box>
  );
};

export default RoutePlannerPanel;
